import { type Discipline, DISCIPLINES, GEAR_SLOTS, type GearSlot, getCalling, getItem, masteriesOf, skill, skillsIn } from '@wyn/content';
import { Link, Navigate } from '@tanstack/react-router';
import type { MeResponse } from '@wyn/contracts';
import { balance, MAX_BLOCKS } from '@wyn/game-core';
import { useState } from 'react';
import { GameShell } from '../components/Layout.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { Icon } from '../components/Icon.tsx';
import { UI } from '../icons/ui-icons.ts';
import { useAction, useBlock, useSetAnalytics, useSetChat, useUnblock } from '../lib/queries.ts';
import { formatDuration, useNow } from '../lib/time.ts';
import { useMe } from '../lib/useMe.ts';
import { Loading } from './Landing.tsx';

const DISCIPLINE_NAME = { martial: 'Martial', craft: 'Craft', cunning: 'Cunning', cloister: 'Cloister' } as const;

export function Character() {
  const me = useMe();
  if (me.isPending) return <Loading />;
  if (!me.data?.player) return <Navigate to="/" />;
  const { player: p, skills, inventory } = me.data;
  const levels = new Map(skills.map((s) => [s.skill, s]));
  return (
    <GameShell me={me.data} skew={me.skew}>
      <div className="space-y-6">
        <section className="panel">
          <h1 className="text-2xl">{p.name}{p.title && <span className="ml-2 text-lg text-gilt">· {p.title}</span>}</h1>
          <p className="text-ink-dim">
            Level {p.level} {getCalling(p.calling).name} · Renown {p.renown.total.toLocaleString('en-GB')}
            {p.renown.toNext !== null && <span className="text-ink-faint"> ({p.renown.into}/{p.renown.toNext} to next)</span>}
          </p>
          <dl className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {(['strength', 'defence', 'speed', 'dexterity'] as const).map((s) => (
              <div key={s}>
                <dt className="smallcaps text-sm text-ink-dim">{s}</dt>
                <dd className="font-mono tabular-nums">{p.stats[s].toLocaleString('en-GB', { maximumFractionDigits: 2 })}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-rule pt-3">
            <TitleChoice />
            <Link to="/deeds" className="nav-link">
              <Icon name={UI.deeds} className="mr-1" />Deeds · {Object.keys(p.record.deeds).length} done
            </Link>
          </div>
        </section>

        <section className="grid gap-4 sm:grid-cols-2">
          {DISCIPLINES.map((d) => (
            <div key={d} className="panel space-y-2">
              <DisciplineHead discipline={d} total={skillsIn(d).reduce((n, s) => n + (levels.get(s.id)?.level ?? 1), 0)} tier={p.mastery[d] ?? 0} />
              <ul className="space-y-1">
                {skillsIn(d).map((s) => {
                  const v = levels.get(s.id);
                  return (
                    <li key={s.id} className="flex justify-between gap-2">
                      <span><Icon name={s.icon} className="mr-2" />{skill(s.id).name}</span>
                      <span className="font-mono text-sm tabular-nums text-ink-dim">{v?.level ?? 1}</span>
                    </li>
                  );
                })}
              </ul>
              <Passives discipline={d} tier={p.mastery[d] ?? 0} />
            </div>
          ))}
        </section>

        <Loadout />

        <section className="panel">
          <h2 className="heading mb-2">Pack</h2>
          {inventory.length === 0 ? (
            <p className="text-ink-faint">Empty.</p>
          ) : (
            <ul className="space-y-1">
              {inventory.map((i) => {
                const item = getItem(i.itemKey);
                return (
                  <li key={`${i.itemKey}#${i.quality}`} className="flex justify-between gap-2">
                    <span><Icon name={item.icon} className="mr-2" />{item.name}<span className="ml-2 text-xs text-ink-faint">{i.quality}</span>
                      {item.tool && <span className="ml-2 text-xs text-ink-dim" title="Held in the pack, the best tool for a skill helps every game of it.">+{Math.round(item.tool.bonus * balance.fight.qualityMult[i.quality] * 100)}% {skill(item.tool.skill).name}</span>}
                    </span>
                    <span className="flex items-center gap-2">
                      {item.use && <UseButton itemKey={i.itemKey} quality={i.quality} />}
                      <span className="font-mono text-sm tabular-nums">×{i.quantity}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <ChatAndBlocks />
        <PlayAnalytics />
      </div>
    </GameShell>
  );
}

/** Go by any title earned, from mastery or a deed; the default is the highest mastery title. */
function TitleChoice() {
  const me = useMe();
  const p = me.data!.player!;
  const set = useAction('setTitle');
  if (p.titles.length === 0) return <span className="text-sm text-ink-faint">No titles yet: earn one from mastery or a deed.</span>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label htmlFor="title-choice" className="text-sm text-ink-dim">Go by</label>
      <select
        id="title-choice"
        className="field"
        value={p.titleChoice ?? ''}
        disabled={set.isPending}
        onChange={(e) => set.mutate({ title: e.target.value || null })}
      >
        <option value="">Highest mastery title</option>
        {p.titles.map((t) => (
          <option key={t.id} value={t.id}>{t.title}</option>
        ))}
      </select>
      <ErrorNotice error={set.error} />
    </div>
  );
}

/** A Discipline's name, its total levels and its title, with a bar to the next tier. */
function DisciplineHead({ discipline, total, tier }: { discipline: Discipline; total: number; tier: number }) {
  const tiers = masteriesOf(discipline);
  const current = tier > 0 ? tiers[tier - 1]! : null;
  const next = tiers[tier] ?? null;
  const from = current?.levels ?? 0;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="heading">{DISCIPLINE_NAME[discipline]}</h2>
        <span className="text-sm">{current ? current.title : <span className="text-ink-faint">no title yet</span>}</span>
      </div>
      <div className="flex items-center gap-2 font-mono text-xs text-ink-faint">
        <span>{total} levels</span>
        {next && (
          <>
            <div className="h-1 flex-1 bg-rule" role="progressbar" aria-label={`${DISCIPLINE_NAME[discipline]} mastery`} aria-valuenow={total} aria-valuemax={next.levels}>
              <div className="h-full bg-gilt" style={{ width: `${Math.min(100, ((total - from) / (next.levels - from)) * 100)}%` }} />
            </div>
            <span>{next.levels} for {next.title}</span>
          </>
        )}
      </div>
    </div>
  );
}

/** The four passives, earned ones bright. */
function Passives({ discipline, tier }: { discipline: Discipline; tier: number }) {
  return (
    <ul className="space-y-0.5 border-t border-rule pt-2 text-xs">
      {masteriesOf(discipline).map((m) => (
        <li key={m.tier} className={m.tier <= tier ? 'text-ink' : 'text-ink-faint'}>
          <span className="font-mono">{m.levels}</span> {m.title}: {m.passive.description}
        </li>
      ))}
    </ul>
  );
}

/** Drink, eat, or wait out the cooldown its group is on. */
function UseButton({ itemKey, quality }: Pick<MeResponse['inventory'][number], 'itemKey' | 'quality'>) {
  const me = useMe();
  const now = useNow();
  const use = useAction('useItem', { refetch: ['me'] });
  const cd = getItem(itemKey).use?.cooldown;
  const readyAt = cd ? (me.data!.player!.cooldowns[cd.group] ?? 0) : 0;
  const wait = readyAt - (now + me.skew);
  return (
    <>
      <ErrorNotice error={use.error} />
      {wait > 0 ? (
        <span className="font-mono text-xs text-ink-faint" title={`One ${cd!.group} at a time`}>{formatDuration(wait)}</span>
      ) : (
        <button type="button" className="btn-quiet py-0 text-xs" disabled={use.isPending} onClick={() => use.mutate({ itemKey, quality })}>
          {cd?.group === 'feast' || cd?.group === 'meal' ? 'eat' : 'drink'}
        </button>
      )}
    </>
  );
}

const SLOT_NAME: Record<GearSlot, string> = { mainHand: 'Main hand', offHand: 'Off hand', ranged: 'Ranged', head: 'Head', body: 'Body', hands: 'Hands', legs: 'Legs', feet: 'Feet', mount: 'Mount' };

/** Worn and wielded gear. A defender fights with it as saved here; an attacker picks a slot each turn. */
function Loadout() {
  const me = useMe();
  const set = useAction('loadout');
  const { player: p, inventory } = me.data!;
  return (
    <section className="panel space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="heading">Arms and armour</h2>
        <span className="text-sm text-ink-faint">You defend yourself with whatever you wear here.</span>
      </div>
      <ul className="grid gap-2 sm:grid-cols-2">
        {GEAR_SLOTS.map((slot) => {
          const worn = p!.loadout[slot];
          const options = inventory.filter((i) => getItem(i.itemKey).slot === slot);
          const value = worn ? `${worn.itemKey}#${worn.quality}` : '';
          return (
            <li key={slot}>
              <label className="flex items-center justify-between gap-2">
                <span className="smallcaps text-sm text-ink-dim">{SLOT_NAME[slot]}</span>
                <select
                  className="field w-48"
                  aria-label={SLOT_NAME[slot]}
                  value={value}
                  disabled={set.isPending || (options.length === 0 && !worn)}
                  onChange={(e) => {
                    const [itemKey, quality] = e.target.value.split('#') as [string, (typeof options)[number]['quality']];
                    set.mutate({ slot, item: e.target.value ? { itemKey, quality } : null });
                  }}
                >
                  <option value="">{slot === 'mainHand' || slot === 'offHand' ? '— bare hand —' : slot === 'mount' ? '— on foot —' : '— nothing —'}</option>
                  {options.map((i) => (
                    <option key={`${i.itemKey}#${i.quality}`} value={`${i.itemKey}#${i.quality}`}>
                      {getItem(i.itemKey).name} ({i.quality})
                    </option>
                  ))}
                </select>
              </label>
            </li>
          );
        })}
      </ul>
      <ErrorNotice error={set.error} />
    </section>
  );
}

/**
 * Whether this player's play goes to analytics (game actions, sent by the server, and their visits if
 * they said yes to the banner). On by default for adults, off for under-18s until they turn it on.
 */
function PlayAnalytics() {
  const p = useMe().data!.player!;
  const set = useSetAnalytics();
  const on = p.analytics === 'on';
  const why = p.analyticsChosen ? 'You chose this.' : on ? 'On by default for players 18 and over.' : 'Off by default for players under 18.';
  return (
    <section className="panel space-y-3" aria-labelledby="play-analytics">
      <h2 id="play-analytics" className="heading">Play analytics</h2>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm">
          Analytics are <strong>{on ? 'on' : 'off'}</strong>.
          <span className="text-ink-faint"> {why} {on ? 'What your character does (not what you say) helps us decide what to improve.' : 'Nothing your character does is sent.'}</span>
        </span>
        <button type="button" className="btn-quiet" disabled={set.isPending} onClick={() => set.mutate({ analytics: on ? 'off' : 'on' })}>
          {on ? 'Turn analytics off' : 'Turn analytics on'}
        </button>
      </div>
      <ErrorNotice error={set.error} />
    </section>
  );
}

/**
 * Chat on or off, and the players this one has blocked. A block hides their lines, takes them out of
 * Folk about town and refuses their offers; they are never told. It does not stop them attacking.
 */
function ChatAndBlocks() {
  const me = useMe();
  const { player: p, blocks } = me.data!;
  const setChat = useSetChat();
  const block = useBlock();
  const unblock = useUnblock();
  const [name, setName] = useState('');
  const on = p!.chat === 'on';
  return (
    <section className="panel space-y-3" aria-labelledby="chat-and-blocks">
      <h2 id="chat-and-blocks" className="heading">Chat and blocks</h2>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm">
          Chat is <strong>{on ? 'on' : 'off'}</strong>.
          <span className="text-ink-faint"> {on ? 'Turn it off to see nothing anyone says.' : 'You see nothing anyone says, and nobody hears you.'}</span>
        </span>
        <button type="button" className="btn-quiet" disabled={setChat.isPending} onClick={() => setChat.mutate({ chat: on ? 'off' : 'on' })}>
          {on ? 'Turn chat off' : 'Turn chat on'}
        </button>
      </div>
      <ErrorNotice error={setChat.error} />

      <div className="space-y-2 border-t border-rule pt-3">
        <p className="text-sm text-ink-dim">
          Block a player and you will not see what they say, they leave your Folk about town, and their trade offers to you are refused. They are not
          told. A block cannot stop an attack.
        </p>
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) block.mutate({ name: name.trim() }, { onSuccess: () => setName('') });
          }}
        >
          <input className="field min-w-0 flex-1" aria-label="Name to block" placeholder="Their name" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} />
          <button type="submit" className="btn" disabled={block.isPending || !name.trim() || blocks.length >= MAX_BLOCKS}>Block</button>
        </form>
        <ErrorNotice error={block.error ?? unblock.error} />
        {blocks.length === 0 ? (
          <p className="text-sm text-ink-faint">You have blocked nobody.</p>
        ) : (
          <ul aria-label="Blocked players" className="space-y-1 text-sm">
            {blocks.map((b) => (
              <li key={b.id} className="flex items-center justify-between gap-2">
                <span>{b.name}</span>
                <button type="button" className="btn-quiet py-0 text-xs" disabled={unblock.isPending} onClick={() => unblock.mutate({ playerId: b.id })}>
                  Unblock<span className="sr-only"> {b.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-ink-faint">{blocks.length} of {MAX_BLOCKS}.</p>
      </div>
    </section>
  );
}
